/**
 * Vehicle AI service — AI-powered features for vehicle listings.
 * Uses Gemini 2.5 Flash via Supabase Edge Functions.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

// ─── Types ─────────────────────────────────────────────────────────────────

interface GenerateDescriptionInput {
  make: string
  model: string
  year: number
  category?: string
  color?: string
  transmission?: string
  fuelType?: string
  seats?: number
  doors?: number
  features?: string[]
  locale?: string
}

interface GeneratedDescription {
  title: string
  description: string
}

export interface PricingSuggestion {
  suggestedDailyRateCents: number | null
  p25: number | null
  median: number | null
  p75: number | null
  count: number
  message?: string
}

// ─── Service ───────────────────────────────────────────────────────────────

class VehicleAIServiceImpl {
  /**
   * Generate a compelling listing title and description using AI.
   * Falls back gracefully — callers should handle errors by keeping manual input.
   */
  async generateDescription(input: GenerateDescriptionInput): Promise<GeneratedDescription> {
    const { data, error } = await supabase.functions.invoke('generate-description', {
      body: input,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Description generation failed',
        code: 'AI_GENERATION_FAILED',
        statusCode: 422,
      })
    }

    return data as GeneratedDescription
  }

  /**
   * Get pricing suggestions based on similar vehicles in the market.
   * Returns null values if not enough comparable data exists.
   */
  async suggestPricing(
    category: string,
    year: number,
    cityId?: string,
  ): Promise<PricingSuggestion> {
    const { data, error } = await supabase.functions.invoke('suggest-pricing', {
      body: { category, year, cityId },
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Pricing suggestion failed',
        code: 'PRICING_SUGGESTION_FAILED',
        statusCode: 422,
      })
    }

    return data as PricingSuggestion
  }
}

export const vehicleAIService = new VehicleAIServiceImpl()
